import {
  SiBehance,
  SiBuymeacoffee,
  SiCredly,
  SiDevdotto,
  SiDigitalocean,
  SiDribbble,
  SiFacebook,
  SiGithub,
  SiInstagram,
  SiLinkedin,
  SiMedium,
  SiNotion,
  SiPatreon,
  SiPaypal,
  SiPocket,
  SiStackoverflow,
  SiTripadvisor,
  SiUnsplash,
  SiX,
  SiYoutube,
} from "react-icons/si"
import { RiExternalLinkLine, RiMailLine } from "react-icons/ri"
import { IconType } from "react-icons/lib"

// Brand marks keep their official filled shape (Simple Icons), set in one colour.
// `name` is the brand's name, used for accessible link names such as "Sam Perrin on GitHub".
export const SOCIAL_RESOURCES: Record<string, { icon: IconType, url: string, name: string }> = {
  behance: { icon: SiBehance, url: "https://www.behance.net", name: "Behance" },
  dribbble: { icon: SiDribbble, url: "https://dribbble.com", name: "Dribbble" },
  linkedin: { icon: SiLinkedin, url: "https://www.linkedin.com/in", name: "LinkedIn" },
  twitter: { icon: SiX, url: "https://x.com", name: "X" },
  facebook: { icon: SiFacebook, url: "https://www.facebook.com", name: "Facebook" },
  instagram: { icon: SiInstagram, url: "https://www.instagram.com", name: "Instagram" },
  devto: { icon: SiDevdotto, url: "https://dev.to", name: "DEV" },
  github: { icon: SiGithub, url: "https://github.com", name: "GitHub" },
  stackoverflow: { icon: SiStackoverflow, url: "https://stackoverflow.com/users", name: "Stack Overflow" },
  youtube: { icon: SiYoutube, url: "https://www.youtube.com/channel", name: "YouTube" },
  medium: { icon: SiMedium, url: "https://medium.com", name: "Medium" },
  notion: { icon: SiNotion, url: "", name: "Notion" },
  unsplash: { icon: SiUnsplash, url: "https://unsplash.com", name: "Unsplash" },
  patreon: { icon: SiPatreon, url: "https://www.patreon.com", name: "Patreon" },
  paypal: { icon: SiPaypal, url: "", name: "PayPal" },
  pocket: { icon: SiPocket, url: "", name: "Pocket" },
  digitalocean: { icon: SiDigitalocean, url: "https://www.digitalocean.com/blog/author", name: "DigitalOcean" },
  tripadvisor: { icon: SiTripadvisor, url: "https://www.tripadvisor.com", name: "Tripadvisor" },
  buymeacoffee: { icon: SiBuymeacoffee, url: "https://www.buymeacoffee.com", name: "Buy Me a Coffee" },
  mailto: { icon: RiMailLine, url: "", name: "Email" },
  url: { icon: RiExternalLinkLine, url: "", name: "Website" },
  credly: { icon: SiCredly, url: "", name: "Credly" },
}

// Profile URL for an author's [network, profile] pair; full URLs and mailto links are used as they are
export const socialProfileUrl = ([network, profile]: string[]): string =>
  /^(?:https?:\/\/|mailto:)/.test(profile)
    ? profile
    : `${SOCIAL_RESOURCES[network]?.url}/${profile}`

// The network for a profile URL, matched on its domain ("https://github.com/x" is "github")
const NETWORK_DOMAINS: Record<string, string> = {
  "github.com": "github",
  "twitter.com": "twitter",
  "x.com": "twitter",
  "linkedin.com": "linkedin",
  "credly.com": "credly",
  "youtube.com": "youtube",
  "medium.com": "medium",
  "dev.to": "devto",
}
export const networkForUrl = (url: string): string | undefined => {
  const host = url.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]
  return NETWORK_DOMAINS[host]
}

export const COOKIE_PROMPT_CONTAINER_ID = "cookie-prompt-container"
export const COOKIE_PROMPT_AGREEMENT = "site-cookie-consent-agreement"
export const COOKIE_PROMPT_DEFAULT_TITLE = "This site uses cookies"
export const COOKIE_PROMPT_DEFAULT_DESCRIPTION = `
We use 3rd party tools to understand visitors and create a
personalized experience. By clicking "Agree" button below,
you agree to allowing us to storage cookies on your browser.
`
export const COOKIE_PROMPT_DEFAULT_READMORE = "Read more on"
export const SUBSCRIPTION_DEFAULT_TITLE = "Subscribe to our email newsletters"
export const SUBSCRIPTION_DEFAULT_DESCRIPTION = `
Stay tuned to our latest content with the ability to opt-out at
anytime. We will not spam your inbox or share your email with any
third parties.
`

export const CUSTOM_EVENT_SUBSCRIPTION = "CUSTOM_EVENT_SUBSCRIPTION"
export const CUSTOM_EVENT_SOCIAL_SHARING = "CUSTOM_EVENT_SOCIAL_SHARING"
export const CUSTOM_EVENT_TOGGLE_THEME = "CUSTOM_EVENT_TOGGLE_THEME"

// Display names for category slugs. Product and brand names keep their own casing;
// anything not listed is shown in sentence case ("home-lab" is "Home lab")
const CATEGORY_NAMES: Record<string, string> = {
  aria: "Aria",
  "aria-automation": "Aria Automation",
  "aria-hub": "Aria Hub",
  "aria-operations": "Aria Operations",
  broadcom: "Broadcom",
  kubernetes: "Kubernetes",
  linux: "Linux",
  til: "TIL",
  vmware: "VMware",
  vra: "vRA",
  vrealize: "vRealize",
  vro: "vRO",
}

export const categoryName = (slug: string): string => {
  if (CATEGORY_NAMES[slug]) return CATEGORY_NAMES[slug]
  const words = slug.replace(/-/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// Perrin tag colours. A tag's colour comes from its label, so a category keeps the same colour
// on every page and in every build. Colours group posts; they never mean good or bad.
// Copied from Perrin's tagTone() (components/bundle.js)
export const TAG_TONES = ["teal", "blue", "violet", "pink", "red", "orange", "amber", "green"] as const
export type TagTone = typeof TAG_TONES[number]

export const tagTone = (label: string): TagTone => {
  const s = String(label || "").trim().toLowerCase()
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return TAG_TONES[h % TAG_TONES.length]
}
